/**
 * KOA-18136 §37 — CAN THIS CANVAS READ BE TRUSTED, AND IS IT STILL THE SAME CANVAS?
 *
 * Both questions are pure, both are asked from two very different places (the
 * provider's model↔diagram reconcile and the DSL executor's command loop), and both
 * had the same answer baked into an `[]`.
 *
 * `BpmnService.getAllElements()` returns an EMPTY ARRAY when there is no modeler —
 * indistinguishable from "the author deleted everything". The subtractive half of the
 * reconcile reads that array as authority ("the diagram no longer has these nodes")
 * and deletes the model's nodes, and since every node LOOKUP runs the reconcile, a
 * canvas that dies mid-script does not merely stop accepting commands: it erases the
 * steps already built. Live session be14b8f5: a 173-line build lost 39 lines — six
 * interactions "Task not found.", five phase assignments "Step not found.", every
 * command from line 143 on "BPMN Modeler is not ready" — and only 8 of the 14 created
 * steps survived in the persisted model, while the flows referencing the missing ones
 * were still there.
 *
 * So: a read is authority only when a modeler answered AND it answered with elements.
 * Anything else is "I cannot see", and code that cannot see may not subtract.
 *
 * The second question is what turns a silent loss into an actionable stop. A canvas
 * that is REPLACED mid-script (a re-import, a different workflow, a remount) is
 * perfectly readable — commands keep succeeding, against a diagram that no longer
 * contains the earlier half of the build. The witness below is how that is caught:
 * the root element's identity plus the ids the script has already created. The same
 * live run shows the fingerprint changing — the final listing's start/end events carry
 * different ids than the ones the script resolved at its first line.
 *
 * Pure by design: no window, no service, no React. The callers do the reading.
 */

/** Whether a canvas read may be treated as authority — and if not, why. */
export type CanvasReadability = "readable" | "modeler-absent" | "empty-read";

/** What a caller observed when it tried to read the canvas. */
export interface ICanvasReadObservation {
  /** Did a live modeler answer the read at all? */
  readonly modelerPresent: boolean;
  /** How many elements the registry returned. */
  readonly elementCount: number;
}

/**
 * Classify a canvas read.
 *
 * `empty-read` is not a hypothetical: an imported BPMN diagram ALWAYS carries at least
 * its process/collaboration root in the element registry, so zero elements means the
 * import never happened (or the modeler is being torn down), never "the author emptied
 * the diagram". A genuine delete-everything still leaves the root — which is why this
 * classification cannot mistake real user work for blindness.
 */
export function classifyCanvasRead(observation: ICanvasReadObservation): CanvasReadability {
  if (!observation.modelerPresent) return "modeler-absent";
  if (observation.elementCount <= 0) return "empty-read";
  return "readable";
}

/**
 * THE LAW: only a readable canvas may authorize removing model nodes.
 *
 * The additive half needs no permission — adopting from a read that returned nothing
 * is a no-op by construction. Subtraction is the destructive direction, and it is the
 * one that was running on blind reads.
 */
export function mayRemoveModelNodes(readability: CanvasReadability): boolean {
  return readability === "readable";
}

/** One-line cause for a diagnostic / tool message. */
export function describeCanvasReadability(readability: CanvasReadability): string {
  if (readability === "modeler-absent") return "no BPMN modeler answered the read";
  if (readability === "empty-read") return "the modeler answered with zero elements (nothing imported)";
  return "readable";
}

/** What the canvas looked like when it was last known good. */
export interface ICanvasWitness {
  /** Identity of the diagram root (process / collaboration element). */
  readonly rootId: string;
  /** Ids that MUST still be present — nodes this script created. */
  readonly requiredIds: readonly string[];
  /**
   * The subset of `requiredIds` that are LANES. Lanes are the one witnessed thing
   * the cleanup is still allowed to remove (an actor lane nobody put a step in), so
   * a narrowed rebase needs to tell them apart from steps — see
   * {@link canvasShrinkScope}.
   */
  readonly laneIds: readonly string[];
}

/** What the canvas looks like now. */
export interface ICanvasSnapshot {
  readonly readability: CanvasReadability;
  readonly rootId: string;
  readonly presentIds: ReadonlySet<string>;
}

/**
 * `intact` — same canvas, everything the script built is still there.
 * `unreadable` — the canvas cannot be read at all (dead or being torn down).
 * `replaced` — a DIFFERENT diagram is mounted; the earlier work is not in it.
 * `lost` — the same diagram, but elements the script created are gone.
 */
export type CanvasContinuity = "intact" | "unreadable" | "replaced" | "lost";

/** Verdict plus the evidence a tool message needs to name the damage. */
export interface ICanvasContinuityVerdict {
  readonly continuity: CanvasContinuity;
  readonly missingIds: readonly string[];
}

/**
 * Compare a witness against the live canvas.
 *
 * Order matters: an unreadable canvas is reported as unreadable, never as "everything
 * was lost" — the ids are not gone, they are unobservable, and telling the author his
 * graph vanished when the canvas merely unmounted sends him rebuilding work that is
 * still on the server.
 */
export function checkCanvasContinuity(
  witness: ICanvasWitness | undefined,
  snapshot: ICanvasSnapshot
): ICanvasContinuityVerdict {
  if (snapshot.readability !== "readable") return { continuity: "unreadable", missingIds: [] };
  if (!witness) return { continuity: "intact", missingIds: [] };
  if (witness.rootId.length > 0 && snapshot.rootId.length > 0 && witness.rootId !== snapshot.rootId) {
    return { continuity: "replaced", missingIds: witness.requiredIds.filter((id) => !snapshot.presentIds.has(id)) };
  }
  const missingIds: string[] = witness.requiredIds.filter((id: string): boolean => !snapshot.presentIds.has(id));
  if (missingIds.length > 0) return { continuity: "lost", missingIds };
  return { continuity: "intact", missingIds: [] };
}

/**
 * WHAT a command is allowed to remove — which is also the exact extent of the
 * forgiveness the continuity check owes it.
 *
 * `any` — the author said DELETE. Whatever went missing, they asked for it.
 * `lanes-only` — `LINT`/`LAYOUT` run the executor's cleanup, and the only witnessed
 *   thing that cleanup removes is an actor lane with no steps in it. It used to also
 *   delete every node standing outside the flow, and this blanket forgiveness is what
 *   made that invisible: the sweep took two authored steps and the loss detector,
 *   told a LINT had just run, rebased them out of the witness (live `16987e7e`).
 *   The sweep is gone (KOA-18136) and so is the forgiveness that covered it.
 * `none` — nothing this command does may shrink the canvas; a loss here is a loss.
 */
export type CanvasShrinkScope = "any" | "lanes-only" | "none";

const EXPLICIT_DELETE_COMMANDS: ReadonlySet<string> = new Set<string>([
  "DELETE",
  "LANE_DELETE",
  "LINKED_DELETE",
  "SUBPROCESS_DELETE"
]);

const CLEANUP_RUNNING_COMMANDS: ReadonlySet<string> = new Set<string>(["LINT", "LAYOUT"]);

/** How much of a shrink this command kind is allowed to account for. */
export function canvasShrinkScope(kind: string): CanvasShrinkScope {
  if (EXPLICIT_DELETE_COMMANDS.has(kind)) return "any";
  if (CLEANUP_RUNNING_COMMANDS.has(kind)) return "lanes-only";
  return "none";
}

/**
 * Re-base a witness on the live canvas: forget ids that are legitimately gone.
 *
 * Needed because some commands are ALLOWED to remove things, and without a rebase the
 * continuity check would report the executor's own sanctioned work as canvas loss — a
 * false stop is as damaging as a missed one. But the forgiveness is only ever as wide
 * as the command's {@link CanvasShrinkScope}: a step that disappears across a LINT is
 * still a loss, and still stops the run.
 */
export function rebaseCanvasWitness(
  witness: ICanvasWitness,
  snapshot: ICanvasSnapshot,
  scope: CanvasShrinkScope
): ICanvasWitness {
  if (snapshot.readability !== "readable" || scope === "none") return witness;
  const laneIds: ReadonlySet<string> = new Set<string>(witness.laneIds);
  const forgivable = (id: string): boolean => scope === "any" || laneIds.has(id);
  const keep = (id: string): boolean => snapshot.presentIds.has(id) || !forgivable(id);
  return {
    rootId: snapshot.rootId.length > 0 ? snapshot.rootId : witness.rootId,
    requiredIds: witness.requiredIds.filter(keep),
    laneIds: witness.laneIds.filter(keep)
  };
}

/**
 * Command kinds whose success adds a NODE the rest of the script depends on.
 *
 * Sequence flows are deliberately absent: the executor's own cleanup de-duplicates
 * flows on purpose, and flow disappearance already has a dedicated audit. What must
 * never silently vanish is a step, gateway, event, or lane.
 */
const CANVAS_NODE_COMMANDS: ReadonlySet<string> = new Set<string>([
  "TASK",
  "SYSTEM",
  "GATEWAY",
  "SUBPROCESS",
  "LINKED",
  "TIMER",
  "BOUNDARY",
  "LANE"
]);

/** True when a successful command of this kind must be added to the witness. */
export function commandCreatesWitnessedNode(kind: string): boolean {
  return CANVAS_NODE_COMMANDS.has(kind);
}
